import { ApiCallbacksContainer } from './api-callbacks-container';
import { CheckoutCallbackWithOptions } from './checkout-callback-with-options';
import {
  AssetStatus,
  AssetStatusChangeCallback,
  CheckoutCallback,
  CheckoutDropInApi,
  CheckoutEnvironment,
  CheckoutSession,
  DeprecatedSingleAssetStatus,
  SessionChangeCallback,
  SignedInChangeCallback,
  SingleAssetStatus
} from './checkout-drop-in-api';
import { CheckoutFrameCommunicationMessage } from './checkout-frame-communication-message';
import { sanitizeEnvironment, sanitizeProductId, sanitizeVendorId } from './identifier-sanitizers';

export class ApiService {
  constructor(private window: Window & { yatta?: CheckoutDropInApi }) {}

  private onSignedInChangeCallbacks: ApiCallbacksContainer<boolean> = { callbacksWithOptions: [] };
  private onAssetStatusChangeCallbacks: { [productId: string]: Record<string, ApiCallbacksContainer<AssetStatus>> } = {};
  private onCheckoutSessionChangeCallbacks: ApiCallbacksContainer<CheckoutSession | null> = { callbacksWithOptions: [] };
  private callbackQueue: Record<number, () => void> = {};
  private callbacksAreQueued: boolean = true;

  publishInterface(messageHandler: (message: CheckoutFrameCommunicationMessage) => void): void {
    const source = 'checkout-drop-in';
    this.window.yatta = {
      startCheckout: (productId, params) => messageHandler({ source, action: 'startCheckout', productId, params }),
      onSignedInChange: (callback, options) => this.registerSignedInChangeCallback(this.sanitizeCallback(callback), this.sanitizeAsyncExecution(options)),
      onAssetStatusChange: (productId, callback, options) =>
        this.registerAssetStatusChangeCallback(
          messageHandler,
          sanitizeProductId(productId),
          this.sanitizeCallback(callback),
          this.sanitizeAsyncExecution(options),
          sanitizeEnvironment(options?.environment)
        ),
      onSessionChange: (vendorId, callback, options) =>
        this.registerCheckoutSessionChangeCallback(
          messageHandler,
          sanitizeVendorId(vendorId),
          this.sanitizeCallback(callback),
          this.sanitizeAsyncExecution(options)
        ),
      signIn: (vendorId, params) => messageHandler({ source, action: 'signIn', vendorId: sanitizeVendorId(vendorId), params }),
      signOut: () => messageHandler({ source, action: 'signOut' }),
      linkAccount: linkedAccount => messageHandler({ source, action: 'linkAccount', linkedAccount }),
      // Yatta internal only:
      startAcademicRegistration: params => messageHandler({ source, action: 'startAcademicRegistration', params })
    };
  }

  private sanitizeCallback<T>(callback: any): T {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be provided as a function.');
    }
    return callback;
  }

  private sanitizeAsyncExecution(options: any): boolean {
    const asyncExecution = options?.asyncExecution;
    if (asyncExecution !== undefined && asyncExecution !== null && asyncExecution !== true && asyncExecution !== false) {
      throw new Error(`AsyncExecution ${JSON.stringify(asyncExecution)} not supported.`);
    }
    return asyncExecution ?? false;
  }

  private registerSignedInChangeCallback(callback: SignedInChangeCallback, async: boolean): void {
    const lastValue = this.onSignedInChangeCallbacks.lastValue;
    const callbackWithOptions = this.buildCallbackWithOptions(callback, async);
    if (lastValue !== undefined) {
      this.executeCallbackWithOptions(callbackWithOptions, lastValue);
    }
    this.onSignedInChangeCallbacks.callbacksWithOptions.push(callbackWithOptions);
  }

  private registerAssetStatusChangeCallback(
    messageHandler: (message: CheckoutFrameCommunicationMessage) => void,
    productId: string,
    callback: AssetStatusChangeCallback,
    async: boolean,
    environment: CheckoutEnvironment
  ): void {
    if (productId === undefined || productId === null) {
      throw new Error('ProductId is missing.');
    }
    const callbackWithOptions = this.buildCallbackWithOptions(callback, async);
    if (this.onAssetStatusChangeCallbacks[productId] !== undefined && this.onAssetStatusChangeCallbacks[productId][environment] !== undefined) {
      const lastValue = this.onAssetStatusChangeCallbacks[productId][environment].lastValue;
      if (lastValue !== undefined) {
        this.executeCallbackWithOptions(callbackWithOptions, lastValue);
      }
      this.onAssetStatusChangeCallbacks[productId][environment].callbacksWithOptions.push(callbackWithOptions);
    } else {
      const status: DeprecatedSingleAssetStatus[] = [];
      const assetStatus: SingleAssetStatus[] = [];
      const callbacks: ApiCallbacksContainer<AssetStatus> = { lastValue: { assetStatus, status }, callbacksWithOptions: [callbackWithOptions] };
      if (this.onAssetStatusChangeCallbacks[productId] !== undefined) {
        this.onAssetStatusChangeCallbacks[productId][environment] = callbacks;
      } else {
        this.onAssetStatusChangeCallbacks[productId] = { [environment]: callbacks };
      }
    }
    messageHandler({ source: 'checkout-drop-in', action: 'registerForAssetStatusChange', productId, environment });
  }

  private registerCheckoutSessionChangeCallback(
    messageHandler: (message: CheckoutFrameCommunicationMessage) => void,
    vendorId: string,
    callback: SessionChangeCallback,
    async: boolean
  ): void {
    const lastValue = this.onCheckoutSessionChangeCallbacks.lastValue;
    const callbackWithOptions = this.buildCallbackWithOptions(callback, async);
    if (lastValue !== undefined) {
      this.executeCallbackWithOptions(callbackWithOptions, lastValue);
    }
    this.onCheckoutSessionChangeCallbacks.callbacksWithOptions.push(callbackWithOptions);
    messageHandler({ source: 'checkout-drop-in', action: 'registerForCheckoutSessionChange', vendorId });
  }

  private buildCallbackWithOptions<T>(callback: CheckoutCallback<T>, async: boolean): CheckoutCallbackWithOptions<T> {
    const int32 = new Uint32Array(1);
    const numberOfRetries = 10;
    let retryCount = 0;

    do {
      this.window.crypto.getRandomValues(int32);
    } while (int32[0] in this.callbackQueue && retryCount++ < numberOfRetries);

    if (int32[0] in this.callbackQueue) {
      throw new Error('Could not generate unique callback id.');
    }

    return { callback, async, id: int32[0] };
  }

  notifySignedInChange(signedIn: boolean): void {
    this.onSignedInChangeCallbacks.lastValue = signedIn;
    for (const callbackWithOptions of this.onSignedInChangeCallbacks.callbacksWithOptions) {
      this.executeCallbackWithOptions(callbackWithOptions, signedIn);
    }
  }

  notifyAssetStatusChange(productId: string, environment: CheckoutEnvironment, assetStatus: AssetStatus): void {
    if (this.onAssetStatusChangeCallbacks[productId] !== undefined && this.onAssetStatusChangeCallbacks[productId][environment] !== undefined) {
      this.onAssetStatusChangeCallbacks[productId][environment].lastValue = assetStatus;
      for (const callbackWithOptions of this.onAssetStatusChangeCallbacks[productId][environment].callbacksWithOptions) {
        this.executeCallbackWithOptions(callbackWithOptions, assetStatus);
      }
    }
  }

  notifyCheckoutSessionChange(checkoutSession: CheckoutSession | null): void {
    this.onCheckoutSessionChangeCallbacks.lastValue = checkoutSession;
    for (const callbackWithOptions of this.onCheckoutSessionChangeCallbacks.callbacksWithOptions) {
      this.executeCallbackWithOptions(callbackWithOptions, checkoutSession);
    }
  }

  private executeCallbackWithOptions<T>(callbackWithOptions: CheckoutCallbackWithOptions<T>, value: T): void {
    if (!callbackWithOptions.async && this.callbacksAreQueued) {
      this.callbackQueue[callbackWithOptions.id] = () => callbackWithOptions.callback(value);
    } else {
      callbackWithOptions.callback(value);
    }
  }

  queueCallbacks(): void {
    this.callbacksAreQueued = true;
  }

  executeCallbacks(): void {
    this.callbacksAreQueued = false;
    for (const id in this.callbackQueue) {
      this.callbackQueue[id]();
    }
    this.callbackQueue = {};
  }
}
